import React, { useState, useEffect, useCallback } from "react";
import { Message, Modal, Button } from "semantic-ui-react";
import RuvixxForm from "../../../components/RuvixxForm";
import CampaignService from "../../../services/Campaign";
import PropTypes from "prop-types";
import { EMAIL_SERVICES, NULL_TEMPLATE } from "../../../constants/Constants";
import store from "store";
import Validate from "../../../components/Validate";
import { isTemplateDisabled } from "../../../helpers/emailHelper";
import { useEmailProvider } from "../../../hooks/useEmailProvider";
import { useEmailTemplate } from "../../../hooks/useEmailTemplate";
import { useFileUpload } from "../../../hooks/useFileUpload";
import EmailProviderSelector from "../../../components/email/EmailProviderSelector";
import EmailTemplateSelector from "../../../components/email/EmailTemplateSelector";
import EmailCCBCCOptions from "../../../components/email/EmailCCBCCOptions";
import EmailFileUpload from "../../../components/email/EmailFileUpload";
import EmailPreview from "../../../components/email/EmailPreview";

const NewCampaignEmailForm = ({
  campaignId,
  onSuccess,
  closeParentModal,
  emailService,
  onlySmtp = false,
}) => {
  const [senderName, setSenderName] = useState(store.get("userAuth").full_name);
  const [fromUser, setFromUser] = useState("");
  const [fromDomain, setFromDomain] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [ccAccountManagers, setCcAccountManagers] = useState(false);
  const [ccExternalAccountManagers, setCcExternalAccountManagers] =
    useState(false);
  const [ccMenuOpen, setCcMenuOpen] = useState(false);
  const [campaignTemplates, setCampaignTemplates] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [sentModal, setSentModal] = useState(false);

  const emailProvider = useEmailProvider({
    emailService,
    onlySmtp,
    campaign: null,
  });

  const templateDisabledFn = useCallback(
    template => {
      return isTemplateDisabled(template, emailProvider.provider);
    },
    [emailProvider.provider]
  );

  const emailTemplate = useEmailTemplate({
    templateDisabledFn,
    campaignTemplates,
  });

  const fileUpload = useFileUpload();

  const fetchCampaign = useCallback(async () => {
    try {
      const campaign = await CampaignService.getCampaign(campaignId);
      setCampaignTemplates(campaign.info.selected_templates || []);
    } catch (error) {
      console.error("Error fetching campaign:", error);
    }
  }, [campaignId]);

  useEffect(() => {
    const init = async () => {
      setFromDomain("email.domain.com"); // TODO: fetch from backend

      try {
        await Promise.all([
          emailProvider.fetchProviders(),
          emailTemplate.fetchTemplates(),
          fetchCampaign(),
          emailTemplate.fetchEmailFooters(),
          emailTemplate.fetchEmailSignatures(),
        ]);
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    };

    init();
  }, []);

  // Clear template if it becomes disabled when provider changes
  useEffect(() => {
    if (emailTemplate.template.id) {
      const disabled = isTemplateDisabled(
        emailTemplate.template,
        emailProvider.provider
      );
      // Only clear if disabled and not already cleared (prevent infinite loop)
      if (disabled && emailTemplate.template.id !== NULL_TEMPLATE.id) {
        emailTemplate.setTemplate({ ...NULL_TEMPLATE });
      }
    }
  }, [emailProvider.provider]);

  const handleSubmit = useCallback(async () => {
    const uploads = fileUpload.files.map(f => f.attachment);
    const { isSMTP, isOutlook } = emailProvider.providerChecks;

    // Validates that the email user part (THIS_PART@domain.com) is valid.
    if (emailProvider.checkFromField && !Validate.emailUsername(fromUser)) {
      setErrorMessage('Invalid value in "From" field');
    } else {
      let fromUserResolved = fromUser;
      if (emailProvider.smtp_config_id) {
        fromUserResolved = emailProvider.smtpAccount.return_addr;
      } else if (isOutlook) {
        fromUserResolved = emailProvider.outlookAliasSelected;
      } else if (emailProvider.provider === EMAIL_SERVICES.MAILGUN) {
        fromUserResolved = `${fromUser}@${emailProvider.mailgunDomainSelected}`;
      }

      try {
        await CampaignService.sendEmailByCampaign({
          campaignId: campaignId,
          template: emailTemplate.template.id,
          attachments: uploads,
          senderName,
          cc,
          bcc,
          ccAccountManagers,
          ccExternalAccountManagers,
          fromUser: fromUserResolved,
          provider: emailProvider.provider,
          footerId: emailTemplate.footerId,
          ...(emailProvider.smtp_config_id && {
            smtp_config_id: emailProvider.smtp_config_id,
          }),
        });
        setSentModal(true);
      } catch (error) {
        setErrorMessage(error.message || "Failed to send email");
      }
    }
  }, [
    fileUpload.files,
    senderName,
    cc,
    bcc,
    ccAccountManagers,
    ccExternalAccountManagers,
    fromUser,
    emailProvider.providerState,
    emailProvider.smtp_config_id,
    emailProvider.smtpAccount,
    emailProvider.outlookAliasSelected,
    emailProvider.checkFromField,
    emailProvider.mailgunDomainSelected,
    emailProvider.provider,
    emailProvider.providerChecks,
    emailTemplate.template.id,
    emailTemplate.footerId,
    campaignId,
  ]);

  const handleChange = useCallback((event, data) => {
    const value = data.type === "checkbox" ? data.checked : data.value;
    const name = data.name;

    switch (name) {
      case "fromUser":
        setFromUser(value);
        break;
      case "senderName":
        setSenderName(value);
        break;
      case "cc":
        setCc(value);
        break;
      case "bcc":
        setBcc(value);
        break;
      case "ccAccountManagers":
        setCcAccountManagers(value);
        break;
      case "ccExternalAccountManagers":
        setCcExternalAccountManagers(value);
        break;
      case "mailgunDomainSelected":
        emailProvider.handleMailgunDomainChange(event, data);
        break;
      case "outlookAliasSelected":
        emailProvider.handleOutlookAliasChange(event, data);
        break;
      default:
        console.warn(`No state setter found for field: ${name}`);
    }
  }, []);

  const handleConfirmationClose = useCallback(() => {
    setSentModal(false);
    closeParentModal();
  }, [closeParentModal]);

  const { isSMTP, isOutlook, isMailgun } = emailProvider.providerChecks;
  let hasFromUserAddress = !!fromUser;
  if (isOutlook) {
    hasFromUserAddress = emailProvider.outlookAliasSelected !== null;
  } else if (isSMTP) {
    hasFromUserAddress = emailProvider.smtp_config_id !== null;
  }

  return (
    <>
      <Modal open={sentModal} onClose={handleConfirmationClose}>
        <Modal.Content>
          <Message info>
            <Message.Header>Emails Sent</Message.Header>
          </Message>
          <Button content="ok" onClick={handleConfirmationClose} />
        </Modal.Content>
      </Modal>
      <RuvixxForm
        ready={
          !!emailTemplate.template.id &&
          !!senderName &&
          hasFromUserAddress &&
          !!emailProvider.provider
        }
        onSubmit={handleSubmit}
        onSuccess={!errorMessage ? onSuccess : () => {}}
        successHeader={"Email Sent"}
        confirmSubmit={true}
        confirmText="Are you sure you want to send this email?"
        ignoreEnterKey={true}
        error={!!errorMessage}
        errorMessage={errorMessage}
      >
        <EmailProviderSelector
          providerOptions={emailProvider.providerOptions}
          providerState={emailProvider.providerState}
          onProviderChange={emailProvider.handleProviderChange}
          senderName={senderName}
          onSenderNameChange={handleChange}
          fromUser={fromUser}
          onFromUserChange={handleChange}
          fromDomain={fromDomain}
          errorMessage={errorMessage}
          isSMTP={isSMTP}
          smtpOptions={emailProvider.smtpOptions}
          smtp_config_id={emailProvider.smtp_config_id}
          onSmtpChange={emailProvider.handleFromChange}
          isOutlook={isOutlook}
          outlookEmailAliases={emailProvider.outlookEmailAliases}
          outlookAliasSelected={emailProvider.outlookAliasSelected}
          onOutlookAliasChange={emailProvider.handleOutlookAliasChange}
          isMailgun={isMailgun}
          mailgunDomainOptions={emailProvider.mailgunDomainOptions}
          mailgunDomainSelected={emailProvider.mailgunDomainSelected}
          onMailgunDomainChange={handleChange}
        />

        <EmailCCBCCOptions
          ccMenuOpen={ccMenuOpen}
          onToggleCCMenu={() => setCcMenuOpen(!ccMenuOpen)}
          ccAccountManagers={ccAccountManagers}
          ccExternalAccountManagers={ccExternalAccountManagers}
          cc={cc}
          bcc={bcc}
          onChange={handleChange}
        />

        <EmailTemplateSelector
          template={emailTemplate.template}
          templateOptions={emailTemplate.templateOptions}
          onTemplateChange={emailTemplate.handleTemplateChange}
          loading={emailTemplate.templates.length === 0}
          footerOptions={emailTemplate.footerOptions}
          onFooterChange={emailTemplate.handleFooterChange}
          signatureOptions={emailTemplate.signatureOptions}
          onSignatureChange={emailTemplate.handleSignatureChange}
        />

        <EmailFileUpload
          uploadInputRef={fileUpload.uploadInputRef}
          files={fileUpload.files}
          onFileChange={fileUpload.handleFileChange}
          onDelete={fileUpload.uploadManagerRef.current.removeUpload}
        />

        <EmailPreview
          subject={emailTemplate.template.subject}
          previewHtml={emailTemplate.previewHtml}
        />
      </RuvixxForm>
    </>
  );
};

NewCampaignEmailForm.propTypes = {
  campaignId: PropTypes.number.isRequired,
  onSuccess: PropTypes.func.isRequired,
  closeParentModal: PropTypes.func,
  emailService: PropTypes.number,
  onlySmtp: PropTypes.bool,
};

export default NewCampaignEmailForm;
